import {IAktion} from "../../models/IAktion.interface";
import * as XLSX from 'xlsx';

interface HeadlineCell {
    fieldName: string;
    displayName: string;
    isDate?: boolean
}

const TITLE_ROWS: HeadlineCell[] = [
    {fieldName: "id", displayName: "ID"},
    {fieldName: "titel", displayName: "Titel"},
    {fieldName: "start", displayName: "Startdatum", isDate: true},
    {fieldName: "ende", displayName: "Enddatum", isDate: true},
    {fieldName: "ort", displayName: "Ort"},
    {fieldName: "veranstalter", displayName: "Veranstalter"},
    {fieldName: "minAlter", displayName: "Mindestalter"},
    {fieldName: "maxAlter", displayName: "Maximalalter"},
    {fieldName: "preis", displayName: "Preis"},
    {fieldName: "soziPreis", displayName: "Sozialpreis"},
    {fieldName: "maxKinder", displayName: "Maximale Teilnehmerzahl"},
    {fieldName: "schwimmabzeichen", displayName: "Schwimmabzeichen"},
    {fieldName: "beschreibung", displayName: "Beschreibung"},
    {fieldName: "mitzubringen", displayName: "Mitzubringen"},
    {fieldName: "aktiv", displayName: "Aktiv"},
]

export function importAktionen(file: File): Promise<IAktion[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, {type: 'array'});
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const parseDate = (dateStr: string | number): number => {
                    if (typeof dateStr === 'number') {
                        return excelDateToJSDate(dateStr);
                    }

                    const [date, time] = dateStr.split(/\s+/);
                    const [day, month, year] = date.split('.');
                    const [hour, minute] = time.split(':');
                    return new Date(+year, +month - 1, +day, +hour, +minute).getTime();

                };

                const aktionen: IAktion[] = jsonData.map((row: any) => {
                    const aktion: any = {};
                    TITLE_ROWS.forEach(title => {
                        const value = row[title.displayName];
                        aktion[title.fieldName] = title.isDate ? parseDate(value) : value;
                    });
                    return aktion as IAktion;
                });

                resolve(aktionen);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
    });
}

export function exportAktionen(aktionen: IAktion[]) {
    const formatDate = (epoch: number): string => {
        const date = new Date(epoch);
        return date.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).replace(',', '');
    };

    const filteredData = aktionen.map(aktion => {
        const filtered: any = {};
        TITLE_ROWS.forEach(title => {
            const value = aktion[title.fieldName as keyof IAktion];
            filtered[title.displayName] = title.isDate ? formatDate(value as number) : value;
        });
        return filtered;
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Aktionen');

    const excelBuffer: any = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
    const data: Blob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});

    const link: HTMLAnchorElement = document.createElement('a');
    link.href = window.URL.createObjectURL(data);
    link.download = 'aktionen.xlsx';
    link.click();
    window.URL.revokeObjectURL(link.href);
}

function excelDateToJSDate(serial: number) {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 30.12.1899 als Startdatum
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return excelEpoch.getTime() + serial * millisecondsPerDay;
}